<template>
    <v-app-bar dense fixed clipped-left app color="primary" dark>
        <v-app-bar-nav-icon v-if="isShowButtonsMode" ref="side-button" @click.stop="$emit('switching')" />
        <v-toolbar-title class="mr-5 align-center logo-title font-weight-medium" @click="$router.push(topUrl)">
            <v-btn icon class="pl-2">
                <v-img :src="image" max-height="2rem" max-width="2rem" class="mr-3" />
            </v-btn>
            <span> {{ adminOneTitle }} </span>
        </v-toolbar-title>
        <v-spacer />
        <div v-if="isShowButtonsMode && isAllowSshConnection" ref="ssh-button" class="profile-btn mt-0 pt-1 mr-5">
            <button-app-control
                ref="ssh-btn"
                :disabled="isLoading || sshLocalDisabled || isSshProcessing"
                :icon="''"
                :label="$t('SSH_Connection')"
                @click="connectSSH()"
            />
        </div>
        <div v-if="isShowButtonsMode && isAllowRdpConnection" ref="rdp-button" class="profile-btn mt-0 pt-1 mr-5">
            <button-download
                ref="download"
                :icon="''"
                :label="'RDP_Connection'"
                :disabled="isLoading || isRdpProcessing"
                :download-url="downloadRdp()"
                @download-start="rdpFileDownloadStart"
                @download-success="rdpFileDownloadSuccess"
                @download-failure="rdpFileDownloadFailure"
            />
        </div>
        <div v-if="isShowButtonsMode && isAllowTokenCopy" ref="password-button" class="profile-btn mt-0 pt-1 mr-5">
            <button-app-control
                ref="password-btn"
                :disabled="isLoading || tokenLocalDisabled || isTokenProcessing"
                :icon="''"
                :label="$t('Token_Provide')"
                @click="copyPassword()"
            />
        </div>
        <v-toolbar-title v-show="isShowButtonsMode" class="omit-name mr-2">
            <span ref="user-name" v-text="userName" />
        </v-toolbar-title>
        <div v-if="isShowButtonsMode" ref="profile-button" class="profile-btn">
            <profile-button :profile-menu="menuList" @clickMenu="doMenuAction" />
        </div>
        <error-dialog
            v-if="errors"
            ref="error-dialog"
            :title="errorTitle"
            :message="errors ? errorCord + ' - ' + errorMessage : ''"
            @clickClose="errorReset()"
        />
    </v-app-bar>
</template>

<script lang="ts">
import { ErrorDialog } from '../../../components/molecules';
import { Vue, Component, Prop } from 'vue-property-decorator';
import { State, Action, Getter } from 'vuex-class';
import { ProfileButton } from '../../molecules';
import type { AuthState } from '../../../store/modules/auth/types';
import { AppContext } from '../../../store/context';
const Profile = AppContext.Profile;
import { ScreenUrl } from '../../../router/url';
import AdminOneIMG from '../../../assets/image/adminone.png';
import { ButtonAppControl, ButtonDownload } from '../../atoms';
import { nextTick } from 'vue';
import ApiUrl from '../../../api/url';
import type { gatewayAllowSetting, GatewayState } from '../../../store/modules/gateway/types';
const namespace = 'auth';

@Component({
    name: 'TheHeaderTitle',
    components: {
        ButtonAppControl,
        ButtonDownload,
        ProfileButton,
        ErrorDialog,
    },
})
export default class TheHeaderTitle extends Vue {
    @Prop({ default: true }) isShowButtons: boolean;
    @State('auth') auth: AuthState;
    @State('gateway') gateway: GatewayState;
    @Getter('id', { namespace }) id: any;
    @Getter('name', { namespace }) name: any;
    @Getter('uid', { namespace }) uid: any;
    @Getter('amIAuthed', { namespace: 'auth' }) amIAuthed: () => boolean;
    @Action('logout', { namespace }) logout: any;
    @Action('dealUpdate', { namespace }) dealUpdate: any;
    @Action('gatewaySettings', { namespace: 'gateway' }) gatewaySettings: any;
    @Action('sshConnectUrl', { namespace: 'gateway' }) sshConnectUrl: any;
    @Action('rdpDownload', { namespace: 'gateway' }) rdpDownload: any;
    @Action('sessionToken', { namespace: 'gateway' }) sessionToken: any;
    @Action('rdpFileDownloadStart', { namespace: 'gateway' }) rdpFileDownloadStart;
    @Action('rdpFileDownloadSuccess', { namespace: 'gateway' }) rdpFileDownloadSuccess;
    @Action('rdpFileDownloadFailure', { namespace: 'gateway' }) rdpFileDownloadFailure;
    @Action('dealError', { namespace: 'gateway' }) dealError;

    topUrl: string = ScreenUrl.TOP;
    menuList = Profile.Menu.LIST;
    userName = '';
    image = AdminOneIMG;
    sshLocalDisabled: boolean = false;
    tokenLocalDisabled: boolean = false;
    isLoading: boolean = false;

    /**
     * lifecycle hooks
     */
    created() {
        this.$watch(
            'auth.isUpdated',
            (newVal: boolean, oldVal: boolean) => {
                this.fetchUserName();
                this.dealUpdate();
            },
            {
                immediate: true,
                deep: true,
            }
        );
    }
    async mounted() {
        this.isLoading = true;
        // ブラウザでリロードした場合に取得した値が消えているので再取得
        if (!this.isGatewaySettingData && this.amIAuthed()) {
            await (this as any).gatewaySettings();
        }
        this.isLoading = false;
    }

    /**
     * getter
     */
    get adminOneTitle(): string {
        return this.$store.state.embedded.adminOneTitle;
    }

    /**
     * getters
     */
    get isShowButtonsMode(): boolean {
        return this.isShowButtons;
    }
    get errors(): boolean {
        return this.gateway.sshConnect.error || this.gateway.rdpFileDownload.error || this.gateway.sessionToken.error;
    }
    get errorTitle(): string {
        if (this.gateway.sshConnect.error) return 'SSH_Connection_Error';
        if (this.gateway.rdpFileDownload.errorCode) return 'RDP_Connection_Error';
        if (this.gateway.sessionToken.errorCode) return 'Password_Copy_Error';
        return '';
    }
    get errorCord(): string {
        return (
            this.gateway.sshConnect.errorCode ||
            this.gateway.rdpFileDownload.errorCode ||
            this.gateway.sessionToken.errorCode
        );
    }
    get errorMessage(): string {
        return (
            this.gateway.sshConnect.errorMsg ||
            this.gateway.rdpFileDownload.errorMsg ||
            this.gateway.sessionToken.errorMsg
        );
    }
    get isGatewaySettingData(): boolean {
        const gatewaySetting = this.gateway;
        if (!gatewaySetting) return false;
        if (!gatewaySetting.gatewayAllowSettings) return false;
        return true;
    }
    get isSshProcessing(): boolean {
        return this.gateway.show.isProcessing || this.gateway.sshConnect.isProcessing;
    }
    get isRdpProcessing(): boolean {
        return this.gateway.show.isProcessing || this.gateway.rdpFileDownload.isProcessing;
    }
    get isTokenProcessing(): boolean {
        return this.gateway.show.isProcessing || this.gateway.sessionToken.isProcessing;
    }
    get gatewayAllowSettings(): gatewayAllowSetting {
        return this.gateway.gatewayAllowSettings;
    }
    get isAllowSshConnection(): boolean {
        if (!this.isGatewaySettingData) return false;
        return this.gatewayAllowSettings.gateway_ssh_enable;
    }
    get isAllowRdpConnection(): boolean {
        if (!this.isGatewaySettingData) return false;
        return this.gatewayAllowSettings.gateway_xrdp_enable;
    }
    get isAllowTokenCopy(): boolean {
        if (!this.isGatewaySettingData) return false;
        return this.gatewayAllowSettings.session_token_copy_enable;
    }

    /**
     * methods
     */
    doMenuAction(callBackEvent: (caller: any) => void): void {
        callBackEvent(this);
    }
    fetchUserName(): void {
        this.userName = this.name() || this.uid();
    }
    // SSH接続機能
    async connectSSH() {
        this.sshLocalDisabled = true;
        await (this as any).sshConnectUrl();
        // URL形式：siogateway:IPアドレス:ポート /user=ユーザーID /passwd=パスワード
        const ssh_url = this.gateway.sshConnectUrl;
        if (!this.gateway.sshConnect.error && typeof ssh_url !== 'undefined') window.location.href = ssh_url;
        this.sshLocalDisabled = false;
    }
    // RDPファイルダウンロード機能
    downloadRdp(): string {
        return ApiUrl.User.RDP_DOWNLOAD;
    }
    //  セッショントークンコピー機能
    async copyPassword() {
        this.tokenLocalDisabled = true;
        await (this as any).sessionToken();
        if (this.gateway.sessionToken.error) {
            this.tokenLocalDisabled = false;
            return;
        }
        let token = this.gateway.token;
        nextTick(() => {
            const elm = document.createElement('div');
            elm.innerText = token;
            document.body.appendChild(elm);

            const selection = window.getSelection();
            const range = document.createRange();

            range.selectNodeContents(elm);
            selection.removeAllRanges();
            selection.addRange(range);

            document.execCommand('copy');

            selection.removeAllRanges();
            document.body.removeChild(elm);

            this.tokenLocalDisabled = false;
        });
    }
    async errorReset() {
        await (this as any).dealError();
    }
}
</script>

<style lang="scss" scoped>
.logo-title {
    cursor: pointer;
}
.profile-btn {
    padding-top: 1rem;
}
.omit-name {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 30rem;
    font-size: 1rem;
}
</style>
